import { useEffect, useRef, useState } from "react";
import api, { fmtNZD as baseNZD, fmtDate, BACKEND_URL } from "@/lib/api";

const fmtNZD = n => (Number(n) < 0 ? `-${baseNZD(-Number(n))}` : baseNZD(n));
import NumberInput from "@/components/NumberInput";
import DateInput from "@/components/DateInput";
import { useAuth } from "@/context/AuthContext";

const SECTIONS = [["materials", "Materials"], ["labour", "Labour"], ["subcontractors", "Subcontractors"]];
const DETAIL = [["total", "Total only"], ["bill", "Bill"], ["items", "Bill items"]];
const GROUPS = [["", "None"], ["worker", "Worker"], ["day", "Day"], ["task", "Task"]];
const DEFAULTS = { use_chargeout: true, margin_mode: "prorata", detail: { materials: "bill", labour: "bill", subcontractors: "bill" },
  labour_group: ["worker", "", ""], hide_pairs: true, title: "", intro: "", footnotes: "", show_no_markup: null, completion: false, completion_text: "", show_estimate: false, show_summary: false };

export function mdHtml(text) {
  const esc = s => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return (text || "").trim().split(/\n\s*\n/).filter(p => p.trim()).map(p => p.split("\n").map(l => {
    let t = esc(l.trim()).replace(/\*\*(.+?)\*\*/g, "<b>$1</b>").replace(/(^|[^*])\*([^*]+?)\*/g, "$1<i>$2</i>");
    return t.startsWith("- ") ? "&bull; " + t.slice(2) : t;
  }).join("<br/>"));
}

function Row({ r }) {
  const pad = { paddingLeft: `${r.depth * 16}px` };
  const strong = r.kind === "head" || r.kind === "subtotal";
  return (
    <tr className={strong ? "font-semibold" : ""}>
      {r.span ? <td colSpan={3} style={pad}>{r.cols[0]}</td> : <><td style={pad}>{r.cols[0]}</td><td>{r.cols[1]}</td><td>{r.cols[2]}</td></>}
      <td className="text-right">{r.kind === "head" ? "" : fmtNZD(r.amount)}</td>
    </tr>
  );
}

function Preview({ rep, logoUrl }) {
  if (!rep) return <div className="opacity-50 text-sm">Pick a project to preview.</div>;
  const Sum = () => (
    <table className="w-full text-sm"><tbody>
      {rep.sections.map(s => <tr key={s.key}><td>{s.title}</td><td className="text-right" data-testid={`rbp-sum-${s.key}`}>{fmtNZD(s.total)}</td></tr>)}
      <tr className="font-bold border-t border-black"><td>Total</td><td className="text-right" data-testid="rbp-grand">{fmtNZD(rep.grand_total)}</td></tr>
    </tbody></table>
  );
  const SumBox = () => (<><Sum />{rep.estimate_line && <div className="text-sm pt-1" data-testid="rbp-estimate">{rep.estimate_line}</div>}</>);
  return (
    <div className="bg-white text-black p-6 rounded shadow text-[13px] space-y-3" data-testid="rb-preview">
      <div className="flex justify-between items-center">
        {logoUrl ? <img src={logoUrl} alt="logo" className="h-14 object-contain" data-testid="rbp-logo" /> : <b>{rep.company.name}</b>}
        <span>{rep.date}</span>
      </div>
      <h1 className="text-2xl font-bold text-center" data-testid="rbp-title">{rep.title}</h1>
      {rep.period && <div className="text-center" data-testid="rbp-period">{rep.period}</div>}
      {rep.no_markup && <div className="text-center text-xs text-neutral-500" data-testid="rbp-no-markup">No markup</div>}
      {mdHtml(rep.intro).map((h, i) => <p key={i} dangerouslySetInnerHTML={{ __html: h }} />)}
      {rep.show_summary && <div className="border border-black" data-testid="rbp-summary"><div className="bg-neutral-200 font-bold px-2 py-1">SUMMARY</div><div className="px-2 py-1"><SumBox /></div></div>}
      {rep.sections.map(s => (
        <div key={s.key} data-testid={`rbp-sec-${s.key}`}>
          <h2 className="font-bold text-base mt-3 mb-1">{s.title}</h2>
          <table className="w-full"><thead><tr className="border-b border-black text-left">
            {s.headers.map((h, i) => <th key={i} className={i === 3 ? "text-right" : ""}>{h}</th>)}
          </tr></thead>
          <tbody>{s.rows.map((r, i) => <Row key={i} r={r} />)}
            <tr className="font-bold border-t border-black"><td>{s.total_label}</td><td data-testid={`rbp-hours-${s.key}`}>{s.total_hours}</td><td></td><td className="text-right" data-testid={`rbp-total-${s.key}`}>{fmtNZD(s.total)}</td></tr>
          </tbody></table>
        </div>
      ))}
      {rep.long && <div className="border-t-2 border-dashed border-neutral-400 text-[10px] text-neutral-400 text-center" data-testid="rbp-pagebreak">new page in PDF</div>}
      <h2 className="font-bold text-base mt-3">Totals</h2><Sum />
      {!rep.show_summary && rep.estimate_line && <div className="text-sm" data-testid="rbp-estimate">{rep.estimate_line}</div>}
      <p className="italic" data-testid="rbp-nogst">All figures exclude GST.</p>
      {rep.hidden_pairs > 0 && <p className="text-xs text-neutral-500">{rep.hidden_pairs} matching credit/rebill pair(s) netted off.</p>}
      {mdHtml(rep.footnotes).map((h, i) => <p key={i} className="text-xs text-neutral-600" dangerouslySetInnerHTML={{ __html: h }} />)}
      {rep.completion && <div data-testid="rbp-completion"><h2 className="font-bold text-base mt-3 mb-1">Project Completion</h2>
        {mdHtml(rep.completion).map((h, i) => <p key={i} className="mb-2" dangerouslySetInnerHTML={{ __html: h }} />)}</div>}
      <div className="text-center text-xs text-neutral-500 border-t pt-2" data-testid="rbp-footer">
        Questions or queries please call {rep.company.phone} or email {rep.company.email}.<br />{rep.company.name}
      </div>
    </div>
  );
}

function ItemList({ items, sel, setSel, idKey, label, testid, isAdmin, onUnreport }) {
  if (!items.length) return <div className="text-xs opacity-50">No items in this period.</div>;
  return (
    <ul className="text-xs space-y-0.5 max-h-48 overflow-auto" data-testid={testid}>
      {items.map(x => (
        <li key={x[idKey]} className="flex items-center gap-2">
          <input type="checkbox" checked={sel.has(x[idKey])} onChange={() => setSel(s => { const n = new Set(s); n.has(x[idKey]) ? n.delete(x[idKey]) : n.add(x[idKey]); return n; })} data-testid={`${testid}-cb-${x[idKey]}`} />
          <span className="flex-1">{label(x)}</span>
          {x.paired && <span className="text-amber-300">pair</span>}
          {x.reported_at && <span className="text-sky-300" data-testid={`${testid}-reported-${x[idKey]}`}>Reported {fmtDate(x.reported_at)}</span>}
          {x.reported_at && isAdmin && <button className="hub-link" onClick={() => onUnreport(x)} data-testid={`${testid}-unreport-${x[idKey]}`}>Unreport</button>}
        </li>
      ))}
    </ul>
  );
}

function Presets({ opts, apply, onReady }) {
  const [list, setList] = useState([]);
  const [pick, setPick] = useState("");
  const load = () => api.get("/report/presets").then(r => { setList(r.data); return r.data; });
  useEffect(() => { load().then(l => { const d = l.find(p => p.is_default); if (d) { setPick(d.id); apply(d.options); } onReady?.(); }); /* eslint-disable-next-line */ }, []);
  async function save() {
    const name = window.prompt("Preset name (e.g. Client summary, Client detailed, Internal):", list.find(p => p.id === pick)?.name || "");
    if (!name) return;
    const { data } = await api.post("/report/presets", { name, options: opts }); await load(); setPick(data.id);
  }
  async function del() { if (pick && window.confirm("Delete this preset?")) { await api.delete(`/report/presets/${pick}`); setPick(""); load(); } }
  return (
    <div className="flex gap-2 items-end flex-wrap">
      <label className="text-xs">Preset
        <select className="input block" value={pick} onChange={e => { setPick(e.target.value); const p = list.find(x => x.id === e.target.value); if (p) apply(p.options); }} data-testid="rb-preset-select">
          <option value="">— Choose preset —</option>{list.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <button className="btn-ghost text-sm" onClick={save} data-testid="rb-preset-save">Save preset</button>
      {pick && <button className="btn-ghost text-sm text-red-400" onClick={del} data-testid="rb-preset-delete">Delete</button>}
    </div>
  );
}

export default function ReportBuilder() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState(""); const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [opts, setOpts] = useState(DEFAULTS);
  const [margins, setMargins] = useState({ materials: 15, labour: 15, subcontractors: 10 });
  const [rates, setRates] = useState({});
  const [manual, setManual] = useState([]);
  const [selB, setSelB] = useState(null); const [selT, setSelT] = useState(null);
  const [data, setData] = useState(null); const [msg, setMsg] = useState(""); const [busy, setBusy] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const seq = useRef(0);

  useEffect(() => {
    api.get("/projects").then(r => setProjects(r.data));
    api.get("/settings").then(r => { const d = r.data || {}; setMargins(m => ({ materials: d.default_margin_materials ?? m.materials, labour: d.default_margin_labour ?? m.labour, subcontractors: d.default_margin_subcontractors ?? m.subcontractors })); });
    api.get("/settings/logo", { responseType: "blob" }).then(r => setLogoUrl(URL.createObjectURL(r.data))).catch(() => {});
  }, []);
  useEffect(() => { setSelB(null); setSelT(null); setRates({}); setOpts(o => ({ ...o, completion_text: "", completion: !!projects.find(p => p.id === pid && (p.status === "Complete" || p.status === "Archived")) })); }, [pid, from, to, projects]);

  const body = () => ({ project_id: pid, date_from: from, date_to: to, bill_ids: selB ? [...selB] : null, ts_keys: selT ? [...selT] : null,
    ...opts, labour_group: opts.labour_group.filter(Boolean), margins, rates, manual });
  async function build() {
    if (!pid) { setData(null); return; }
    const n = ++seq.current;
    try {
      const { data: d } = await api.post("/report/build", body());
      if (n !== seq.current) return;
      setData(d);
      if (!selB) setSelB(new Set(d.items.bills.filter(b => b.selected).map(b => b.id)));
      if (!selT) setSelT(new Set(d.items.labour.filter(e => e.selected).map(e => e.key)));
    } catch (e) { setMsg(e?.response?.data?.detail || "Couldn't build report"); }
  }
  useEffect(() => { const t = setTimeout(build, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [pid, from, to, opts, margins, rates, manual, selB, selT]);

  const setO = (k, v) => setOpts(o => ({ ...o, [k]: v }));
  function applyPreset(o) {
    setOpts({ ...DEFAULTS, ...o, hide_pairs: true, labour_group: [...(o.labour_group || []), "", "", ""].slice(0, 3) });
    if (o.margins) setMargins(o.margins);
    if (o.date_range === "previous_week") api.get("/report/previous-week").then(r => { setFrom(r.data.from); setTo(r.data.to); });
  }
  const items = data?.items || { bills: [], labour: [], workers: [] };
  function selectAll(on, onlyUnreported = false) {
    setSelB(new Set(on ? items.bills.filter(b => !onlyUnreported || !b.reported_at).map(b => b.id) : []));
    setSelT(new Set(on ? items.labour.filter(e => !onlyUnreported || !e.reported_at).map(e => e.key) : []));
  }
  async function unreport(kind, x) {
    const reason = window.prompt("Reason for unreporting (required):"); if (reason === null) return;
    try { await api.post("/report/unreport", kind === "bill" ? { kind, id: x.id, reason } : { kind, key: x.key, reason }); setMsg("Unreported.");
      if (kind === "bill") setSelB(s => new Set([...(s || []), x.id])); else setSelT(s => new Set([...(s || []), x.key])); }
    catch (e) { setMsg(e?.response?.data?.detail || "Couldn't unreport"); }
  }
  async function download() {
    setBusy(true); setMsg("");
    const win = window.open("", "_blank");
    try {
      const r = await api.post("/report/pdf", body(), { responseType: "blob" });
      const u = URL.createObjectURL(r.data); if (win) win.location.href = u; else window.open(u, "_blank");
      const rep = data.report; const nb = rep.bill_ids.length, nt = rep.ts_keys.length;
      if ((nb || nt) && window.confirm(`Mark these ${nb} bills and ${nt} timesheet entries as Reported?`)) {
        const { data: m } = await api.post("/report/mark-reported", { bill_ids: rep.bill_ids, ts_keys: rep.ts_keys });
        setMsg(`Marked ${m.bills} bill(s) and ${m.entries} timesheet entr(ies) as Reported.`); setSelB(null); setSelT(null);
      }
    } catch (e) { if (win) win.close(); setMsg("Couldn't create the PDF"); }
    setBusy(false);
  }

  async function reread(ids) {
    let added = 0, check = 0, none = 0;
    for (let i = 0; i < ids.length; i++) {
      setMsg(`Reading line items ${i + 1} of ${ids.length}…`);
      try { const { data: r } = await api.post(`/bills/${ids[i]}/reread-lines`);
        if (r.status === "added") added++; else if (r.status === "check_lines") check++; else none++; }
      catch { none++; }
    }
    setMsg(`Line items added to ${added} bill(s)${check ? `, ${check} marked Check lines (didn't add up to the bill total)` : ""}${none ? `, ${none} with no lines found` : ""}.`);
    build();
  }
  const setGroup = (i, v) => { const g = [...opts.labour_group]; g[i] = v; if (!v) for (let j = i + 1; j < 3; j++) g[j] = ""; setO("labour_group", g); };
  const secItems = k => k === "labour" ? null : items.bills.filter(b => b.section === k);
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="report-builder">
      <div className="space-y-3">
        <div className="card flex gap-2 items-end flex-wrap">
          <label className="text-xs">Project
            <select className="input block" value={pid} onChange={e => setPid(e.target.value)} data-testid="rb-project">
              <option value="">— Pick project —</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select></label>
          <label className="text-xs">From<DateInput className="input block" value={from} onChange={e => setFrom(e.target.value)} data-testid="rb-from" /></label>
          <label className="text-xs">To<DateInput className="input block" value={to} onChange={e => setTo(e.target.value)} data-testid="rb-to" /></label>
          <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={opts.use_chargeout} onChange={e => setO("use_chargeout", e.target.checked)} data-testid="rb-chargeout" />Use charge-out rates</label>
        </div>
        <div className="card space-y-2">
          <Presets opts={{ ...opts, margins }} apply={applyPreset} />
          <div className="flex gap-2 flex-wrap text-sm">
            <button className="btn-ghost" onClick={() => selectAll(true)} data-testid="rb-select-all">Select all</button>
            <button className="btn-ghost" onClick={() => selectAll(true, true)} data-testid="rb-select-unreported">Select previously unreported items</button>
            <button className="btn-ghost" onClick={() => selectAll(false)} data-testid="rb-select-none">Clear</button>
          </div>
          <div className="flex gap-4 flex-wrap text-sm">
            <span className="opacity-70">Margin display:</span>
            {[["prorata", "Pro rata (built into each line)"], ["total", "Total (visible margin line)"]].map(([v, l]) => (
              <label key={v} className="flex items-center gap-1"><input type="radio" checked={opts.margin_mode === v} onChange={() => setO("margin_mode", v)} data-testid={`rb-mode-${v}`} />{l}</label>))}
          </div>
          <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={opts.hide_pairs} onChange={e => setO("hide_pairs", e.target.checked)} data-testid="rb-hide-pairs" />Hide matching credit/rebill pairs</label>
          {data?.report?.no_items?.missing > 0 && <div className="text-xs text-amber-300 flex flex-wrap items-center gap-2" data-testid="rb-no-items-note">
            {data.report.no_items.missing} of {data.report.no_items.of} selected bills have no item lines (they print as a single bill row).
            {isAdmin && data.report.no_items.rereadable.length > 0 && <button className="hub-link" onClick={() => reread(data.report.no_items.rereadable)} data-testid="rb-reread-bulk">Re-read line items ({data.report.no_items.rereadable.length} with original stored)</button>}
          </div>}
        </div>
        {SECTIONS.map(([k, t]) => (
          <div key={k} className="card space-y-2" data-testid={`rb-section-${k}`}>
            <div className="flex justify-between items-center flex-wrap gap-2">
              <h3 className="text-silver">{t}</h3>
              <label className="text-xs">Margin %<NumberInput className="input w-20 inline-block ml-1" value={margins[k]} onChange={e => setMargins(m => ({ ...m, [k]: parseFloat(e.target.value || 0) }))} data-testid={`rb-margin-${k}`} /></label>
            </div>
            <div className="flex gap-3 text-xs flex-wrap"><span className="opacity-70">{k === "labour" ? "Labour bills:" : "Detail:"}</span>
              {DETAIL.map(([v, l]) => <label key={v} className="flex items-center gap-1"><input type="radio" name={`d-${k}`} checked={opts.detail[k] === v} onChange={() => setO("detail", { ...opts.detail, [k]: v })} data-testid={`rb-detail-${k}-${v}`} />{l}</label>)}
            </div>
            {k === "labour" ? (<>
              <div className="flex gap-2 flex-wrap text-xs items-center">
                {["First break down by", "and then by", "and then by"].map((l, i) => (
                  <label key={i}>{l}<select className="input block" value={opts.labour_group[i] || ""} disabled={i > 0 && !opts.labour_group[i - 1]} onChange={e => setGroup(i, e.target.value)} data-testid={`rb-group-${i}`}>
                    {GROUPS.filter(([v]) => !v || v === opts.labour_group[i] || !opts.labour_group.some((x, j) => j !== i && x === v)).map(([v, gl]) => <option key={v} value={v}>{gl}</option>)}</select></label>))}
              </div>
              {items.workers.length > 0 && <div className="text-xs space-y-1" data-testid="rb-worker-rates">
                <div className="opacity-70">Charge-out rate per worker (this report only)</div>
                {items.workers.map(w => <div key={w.id} className="flex items-center gap-2"><span className="w-40">{w.name}</span>
                  <NumberInput className="input w-24" value={rates[w.id] ?? w.rate} onChange={e => setRates(r => ({ ...r, [w.id]: parseFloat(e.target.value || 0) }))} data-testid={`rb-rate-${w.id}`} />/h</div>)}
              </div>}
              <ItemList items={items.labour} sel={selT || new Set()} setSel={f => setSelT(s => f(s || new Set()))} idKey="key" testid="rb-labour" isAdmin={isAdmin}
                label={e => `${fmtDate(e.date)} · ${e.worker_name} · ${e.task} · ${e.hours} h`} onUnreport={x => unreport("ts", x)} />
            </>) : null}
            {secItems(k) !== null || k === "labour" ? (
              <ItemList items={k === "labour" ? items.bills.filter(b => b.section === "labour") : secItems(k)} sel={selB || new Set()} setSel={f => setSelB(s => f(s || new Set()))} idKey="id" testid={`rb-bills-${k}`} isAdmin={isAdmin}
                label={b => <>{`${fmtDate(b.date)} · ${b.supplier} ${b.number} · ${fmtNZD(b.total)}`}{b.line_items?.length ? ` · ${b.line_items.length} items` : <span className="opacity-60"> · no item lines</span>}
                  {b.lines_check === "check" && <span className="text-amber-300" data-testid={`rb-check-lines-${b.id}`}> · Check lines</span>}
                  {isAdmin && !b.line_items?.length && b.has_original && <button className="hub-link ml-1" onClick={() => reread([b.id])} data-testid={`rb-reread-${b.id}`}>Re-read line items</button>}</>} onUnreport={x => unreport("bill", x)} />) : null}
          </div>
        ))}
        <div className="card space-y-2">
          <h3 className="text-silver">Manual items</h3>
          {manual.map((m, i) => (
            <div key={i} className="flex gap-2 flex-wrap">
              <input className="input flex-1" placeholder="Description" value={m.description} onChange={e => setManual(x => x.map((y, j) => j === i ? { ...y, description: e.target.value } : y))} data-testid={`rb-manual-desc-${i}`} />
              <NumberInput className="input w-28" placeholder="Amount" value={m.amount} onChange={e => setManual(x => x.map((y, j) => j === i ? { ...y, amount: parseFloat(e.target.value || 0) } : y))} data-testid={`rb-manual-amt-${i}`} />
              <select className="input" value={m.section} onChange={e => setManual(x => x.map((y, j) => j === i ? { ...y, section: e.target.value } : y))}>{SECTIONS.map(([k, t]) => <option key={k} value={k}>{t}</option>)}</select>
              <button className="btn-ghost text-xs text-red-400" onClick={() => setManual(x => x.filter((_, j) => j !== i))}>Remove</button>
            </div>))}
          <button className="btn-ghost text-sm" onClick={() => setManual(x => [...x, { description: "", amount: 0, section: "materials" }])} data-testid="rb-add-manual">+ Add manual item</button>
        </div>
        <div className="card space-y-2">
          <label className="text-xs block">Title<input className="input w-full" value={opts.title} placeholder={data?.report?.title || "Report title"} onChange={e => setO("title", e.target.value)} data-testid="rb-title" /></label>
          <label className="text-xs block">Introduction (Markdown: **bold**, *italic*, - list)<textarea className="input w-full h-20" value={opts.intro} onChange={e => setO("intro", e.target.value)} data-testid="rb-intro" /></label>
          <label className="text-xs block">Footnotes<textarea className="input w-full h-16" value={opts.footnotes} onChange={e => setO("footnotes", e.target.value)} data-testid="rb-footnotes" /></label>
          <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={opts.show_no_markup ?? !!data?.report?.all_zero} onChange={e => setO("show_no_markup", e.target.checked)} data-testid="rb-no-markup" />Show 'No markup' note</label>
          <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={!!opts.show_summary} onChange={e => setO("show_summary", e.target.checked)} data-testid="rb-show-summary" />Show summary at top</label>
          {data?.report?.is_estimate && <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={opts.show_estimate} onChange={e => setO("show_estimate", e.target.checked)} data-testid="rb-show-estimate" />Show estimate on report</label>}
          <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={opts.completion} onChange={e => setO("completion", e.target.checked)} data-testid="rb-completion" />Include completion summary{data?.report?.project_complete ? " (project is Complete)" : ""}</label>
          {opts.completion && <label className="text-xs block">Project Completion text (from the template in Configuration → Settings; edit for this report)
            <textarea className="input w-full h-48" value={opts.completion_text || data?.report?.completion_default || ""} onChange={e => setO("completion_text", e.target.value)} data-testid="rb-completion-text" /></label>}
        </div>
      </div>
      <div className="space-y-2 xl:sticky xl:top-2 self-start">
        <div className="flex gap-2 items-center flex-wrap">
          <button className="btn-silver" disabled={!data || busy || !data.report.sections.length} onClick={download} data-testid="rb-download">{busy ? "Creating…" : "Download report PDF"}</button>
          {msg && <span className="text-sm" data-testid="rb-msg">{msg}</span>}
        </div>
        <Preview rep={data?.report} logoUrl={logoUrl} />
      </div>
    </div>
  );
}

export const _unused = BACKEND_URL;
